import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { PICKER_MINUTES } from './calendarConstants';
import { detectConflicts } from './hooks/conflictUtils';

/**
 * Scheduling fields (start/end time, service bay, employees) with live
 * conflict detection, for scheduling a work order while creating it.
 *
 * Pure controlled component: all data comes in via props, nothing is read
 * from the calendar Zustand store — the mobile calendar keeps these
 * resources in local component state, not in the store.
 *
 * value: { startDateTime: Date, endDateTime: Date, serviceBayId: number|'', employeeIds: number[] }
 * resources: { employees, serviceBays, events, unavailabilities } from the calendar view
 */
function ScheduleSlotFields({ t, value, onChange, resources }) {
  const { employees = [], serviceBays = [], events = [], unavailabilities = [] } = resources || {};
  const [conflicts, setConflicts] = useState([]);

  // Conflict detection - check for overlapping schedules
  useEffect(() => {
    if (!value?.startDateTime || !value?.endDateTime) {
      setConflicts([]);
      return;
    }

    const newConflicts = detectConflicts({
      plannedStart: value.startDateTime,
      plannedEnd: value.endDateTime,
      employeeIds: value.employeeIds,
      serviceBayId: value.serviceBayId,
      existingEvents: events,
      unavailabilities,
      excludeEventId: null,
      employees,
      serviceBays,
      t,
    });

    setConflicts(newConflicts);
  }, [value?.startDateTime, value?.endDateTime, value?.employeeIds, value?.serviceBayId, events, unavailabilities, employees, serviceBays, t]);

  const handleFieldChange = (field, fieldValue) => {
    const newData = { ...value, [field]: fieldValue };

    // Ensure end time is always after start time (minimum picker step)
    if (field === 'startDateTime' && fieldValue && newData.endDateTime) {
      if (newData.endDateTime <= fieldValue) {
        newData.endDateTime = new Date(fieldValue.getTime() + PICKER_MINUTES * 60 * 1000);
      }
    }
    if (field === 'endDateTime' && fieldValue && newData.startDateTime) {
      const minEnd = new Date(newData.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000);
      if (fieldValue < minEnd) {
        newData.endDateTime = minEnd;
      }
    }

    onChange(newData);
  };

  const handleEmployeeToggle = (employeeId) => {
    onChange({
      ...value,
      employeeIds: value.employeeIds.includes(employeeId)
        ? value.employeeIds.filter((id) => id !== employeeId)
        : [...value.employeeIds, employeeId],
    });
  };

  const hardConflicts = conflicts.filter((c) => c.type !== 'work_hours');
  const workHoursConflicts = conflicts.filter((c) => c.type === 'work_hours');

  return (
    <Box data-testid="schedule-section">
      {/* Conflict Warning */}
      {hardConflicts.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          data-testid="schedule-conflict-warning"
          sx={{ mb: 2.5, borderRadius: '8px' }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{t('Conflict detected')}</Typography>
          {hardConflicts.map((conflict, idx) => (
            <Typography key={idx} sx={{ fontSize: '13px' }}>
              {conflict.message}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Work hours info */}
      {workHoursConflicts.length > 0 && (
        <Alert severity="info" sx={{ mb: 2.5, borderRadius: '8px' }}>
          {workHoursConflicts.map((conflict, idx) => (
            <Typography key={idx} sx={{ fontSize: '13px' }}>
              {conflict.message}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Start and End DateTime Row */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Start time')}</Typography>
            <DateTimePicker
              value={value.startDateTime}
              onChange={(newValue) => handleFieldChange('startDateTime', newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  'data-testid': 'schedule-start-input',
                  sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                },
              }}
              ampm={false}
              minutesStep={PICKER_MINUTES}
              format="dd.MM.yyyy HH:mm"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('End time')}</Typography>
            <DateTimePicker
              value={value.endDateTime}
              onChange={(newValue) => handleFieldChange('endDateTime', newValue)}
              minDateTime={value.startDateTime ? new Date(value.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000) : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  'data-testid': 'schedule-end-input',
                  sx: { '& .MuiOutlinedInput-root': { borderRadius: '10px' } },
                },
              }}
              ampm={false}
              minutesStep={PICKER_MINUTES}
              format="dd.MM.yyyy HH:mm"
            />
          </Box>
        </Box>
      </LocalizationProvider>

      {/* Service Bay */}
      {serviceBays.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Service bay')}</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={value.serviceBayId}
              onChange={(e) => handleFieldChange('serviceBayId', e.target.value)}
              displayEmpty
              data-testid="schedule-bay-select"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="">
                <Typography color="text.secondary">{t('Not selected')}</Typography>
              </MenuItem>
              {serviceBays.map((bay) => (
                <MenuItem key={bay.id} value={bay.id}>
                  {bay.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Employees */}
      <Box sx={{ mb: 2.5 }} data-testid="schedule-employee-select">
        <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Employees')}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {employees.map((emp) => (
            <Box
              key={emp.id}
              onClick={() => handleEmployeeToggle(emp.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                px: 1.5,
                borderRadius: '8px',
                border: 1,
                borderColor: value.employeeIds.includes(emp.id) ? 'secondary.main' : 'divider',
                bgcolor: value.employeeIds.includes(emp.id) ? 'rgba(49, 130, 206, 0.08)' : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: 'secondary.main',
                },
              }}
            >
              <Checkbox
                checked={value.employeeIds.includes(emp.id)}
                size="small"
                sx={{ p: 0 }}
              />
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: emp.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                }}
              >
                {emp.initials}
              </Box>
              <Typography sx={{ fontSize: '13px' }}>{emp.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default ScheduleSlotFields;
